import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['home', 'services', 'about', 'solutions', 'contact', 'blog'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleCallClick = () => {
    window.open('https://wa.me/923379913671', '_blank');
  };

  const handleVisitClick = () => {
    window.open('https://www.google.com/maps/search/Midway+Rawalpindi+Pakistan', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/51e5145f106a45f8cb71e40c8e7cc05e/d0b833b3237dbb7c188059d6e6857cdf.png" 
              alt="Northern Tech Logo" 
              className="h-10 w-auto"
            />
            <span className={`text-xl font-semibold transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>Northern Tech</span>
          </div>
          <div className="flex items-center gap-8">
            {['home', 'services', 'about', 'solutions', 'contact', 'blog'].map((section) => (
              <button
                key={section}
                onClick={() => scrollToSection(section)}
                className={`text-sm font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
                } ${activeSection === section ? (scrolled ? 'text-teal-600' : 'text-white') : ''}`}
              >
                {section}
              </button>
            ))}
            <a
              href="https://wa.me/923379913671"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                scrolled ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
              }`}
            >
              <i className="ri-whatsapp-line text-lg"></i>
              <span className="text-sm">WhatsApp</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/445c495465278503355c3d12f88066e1.jpg" 
            alt="IT Infrastructure Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <div className="inline-block px-6 py-2 border border-white/30 rounded-full mb-8 backdrop-blur-sm">
            <span className="text-white/90 text-xs font-medium tracking-widest uppercase">Serving Rawalpindi & Beyond</span>
          </div>
          
          <h1 className="text-7xl md:text-8xl font-light text-white mb-6 leading-tight">
            Complete IT Solutions<br />
            Infrastructure & Repair<br />
            Door-to-Door Service
          </h1>
          
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto leading-relaxed">
            IT support, infrastructure setup, hardware repair & IT asset sales<br />Trusted by 50+ companies in Rawalpindi, Pakistan
          </p>
          
          <button 
            onClick={() => scrollToSection('contact')}
            className="group inline-flex items-center gap-4 bg-gradient-to-r from-teal-500 to-green-500 text-white px-10 py-5 rounded-full text-base font-medium shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <span className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-customer-service-2-line text-xl"></i>
            </span>
            <span>REQUEST SERVICE</span>
            <i className="ri-arrow-right-up-line text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
          </button>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20">
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-4">OUR IT SERVICES</p>
            <h2 className="text-6xl font-bold text-gray-900 leading-tight">
              Complete IT Solutions<br />For Your Business
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-tools-line',
                title: 'IT Infrastructure & Repair',
                description: 'Complete infrastructure setup, hardware repair, and maintenance services. We fix computers, servers, networking equipment, and all IT hardware with expert technicians.'
              },
              {
                icon: 'ri-shopping-cart-2-line',
                title: 'IT Asset Sales',
                description: 'Quality IT equipment and hardware sales. Computers, laptops, servers, networking devices, and all IT assets at competitive prices with warranty support.'
              },
              {
                icon: 'ri-truck-line',
                title: 'Door-to-Door Service',
                description: 'Convenient on-site support at your location. Our technicians come to your office or home in Rawalpindi and surrounding areas for repairs and installations.'
              }
            ].map((service, index) => (
              <div 
                key={index}
                className="group bg-white rounded-3xl p-10 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer border-t-4 border-transparent hover:border-teal-500"
              >
                <div className="w-14 h-14 flex items-center justify-center mb-8">
                  <i className={`${service.icon} text-5xl text-teal-600`}></i>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=IT%20technician%20working%20on%20server%20rack%20installation%20in%20modern%20data%20center%20professional%20network%20infrastructure%20setup%20organized%20cables%20switches%20routers%20Pakistan%20IT%20service%20technical%20work&width=600&height=800&seq=about-it-service-tech&orientation=portrait" 
                  alt="IT Infrastructure Services"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div>
              <div className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-medium mb-6">
                MIDWAY, RAWALPINDI
              </div>
              <h2 className="text-5xl font-serif text-gray-900 mb-8 leading-tight">
                Your Trusted IT Partner<br />in Rawalpindi
              </h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Northern Tech is Rawalpindi's leading IT solutions provider, specializing in infrastructure setup, hardware repair, and IT asset sales. Based in Midway, Rawalpindi, we serve businesses across the region.
                </p>
                <p>
                  With over 50 company clients trusting us for their IT needs, we've built our reputation on quality service, technical expertise, and reliable door-to-door support. From small businesses to large enterprises, we provide complete IT solutions.
                </p>
                <div className="border-l-4 border-teal-500 pl-6 italic text-2xl font-serif text-gray-800 my-8">
                  "Quality IT services delivered right to your doorstep in Rawalpindi."
                </div>
                <p>
                  Our services include complete IT infrastructure installation, computer and server repairs, networking solutions, hardware sales, and ongoing technical support. We come to your location, diagnose issues quickly, and provide efficient solutions.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6 mt-10">
                <div className="bg-teal-50 rounded-2xl p-6">
                  <p className="text-4xl font-bold text-teal-600 mb-2">50+</p>
                  <p className="text-sm text-gray-700">Company Clients</p>
                </div>
                <div className="bg-green-50 rounded-2xl p-6">
                  <p className="text-4xl font-bold text-green-600 mb-2">100%</p>
                  <p className="text-sm text-gray-700">On-Site Service</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section id="solutions" className="py-32 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-6xl font-serif text-gray-900 mb-20 leading-tight">
            IT Services & Products<br />We Offer
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: 'Network Infrastructure',
                tag: 'Setup & Installation'
              },
              {
                name: 'Hardware Repair',
                tag: 'All IT Equipment'
              },
              {
                name: 'IT Equipment Sales',
                tag: 'Quality Products'
              }
            ].map((solution, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
              >
                <div className="bg-white p-12 flex items-center justify-center" style={{ height: '340px' }}>
                  <img 
                    src="https://media.istockphoto.com/id/2200128716/photo/ai-powers-big-data-analysis-and-automation-workflows-showcasing-neural-networks-and-data.jpg?s=612x612&w=0&k=20&c=z7kdn14AJhMX8JlenABkc3mT-df-V0JhxJzqnQ8Q81w="
                    alt={solution.name}
                    className="w-full h-full object-contain object-top"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-serif text-gray-900 mb-3">{solution.name}</h3>
                  <span className="inline-block px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                    {solution.tag}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-32 bg-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="ri-star-fill text-teal-700 text-xl"></i>
              <span className="text-teal-700 text-sm uppercase tracking-widest">CLIENT SUCCESS STORIES</span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900">Trusted by Rawalpindi Businesses</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-teal-50 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-green-500 rounded-full"></div>
                <div>
                  <p className="font-semibold text-gray-900">Ahmed Khan</p>
                  <p className="text-sm text-gray-600">CEO, TechVision Solutions</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-4xl text-teal-600 mb-4 block"></i>
              <p className="text-gray-700 leading-relaxed">
                "Northern Tech set up our entire office network infrastructure. Their door-to-door service made everything so convenient. Highly professional team!"
              </p>
            </div>

            <div className="bg-teal-50 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full"></div>
                <div>
                  <p className="font-semibold text-gray-900">Fatima Malik</p>
                  <p className="text-sm text-gray-600">Manager, Retail Plus</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-4xl text-teal-600 mb-4 block"></i>
              <p className="text-gray-700 leading-relaxed">
                "They repaired our server when it crashed and saved all our data. Fast response and excellent technical knowledge. Best IT service in Rawalpindi!"
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden" style={{ height: '280px' }}>
              <img 
                src="https://readdy.ai/api/search-image?query=Happy%20Pakistani%20business%20owner%20in%20modern%20office%20smiling%20satisfied%20with%20IT%20services%20professional%20portrait%20natural%20lighting%20confident%20expression%20corporate%20photography&width=400&height=500&seq=testimonial-client&orientation=portrait"
                alt="Satisfied Client"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/90 via-teal-900/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="font-semibold text-lg">Usman Ali</p>
                <p className="text-sm text-white/80 mb-2">Director, Al-Noor Enterprises</p>
                <p className="text-sm leading-relaxed">"Reliable IT partner for our business."</p>
              </div>
            </div>

            <div className="md:col-span-2 relative rounded-2xl overflow-hidden" style={{ height: '240px' }}>
              <img 
                src="https://readdy.ai/api/search-image?query=IT%20technician%20helping%20business%20client%20in%20Pakistan%20office%20setting%20professional%20consultation%20technical%20assistance%20friendly%20service%20natural%20lighting%20collaborative%20atmosphere&width=800&height=400&seq=testimonial-support&orientation=landscape"
                alt="IT Support Consultation"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 via-teal-900/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8 text-white max-w-2xl">
                <p className="font-semibold text-xl mb-1">Sarah Iqbal</p>
                <p className="text-sm text-white/80 mb-3">IT Manager, Education Hub</p>
                <p className="text-base leading-relaxed">
                  "We purchased all our computers and networking equipment from Northern Tech. Great prices and they installed everything at our location. Excellent service!"
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <div className="bg-teal-50 rounded-2xl p-6 flex-1">
                <p className="text-gray-700 leading-relaxed mb-4">
                  "Quick repair service and fair pricing. They came to our office and fixed everything on-site."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-500 rounded-full"></div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Bilal Hassan</p>
                    <p className="text-xs text-gray-600">Owner, Print Solutions</p>
                  </div>
                </div>
              </div>
              <div className="bg-teal-50 rounded-2xl p-6 flex-1">
                <p className="text-gray-700 leading-relaxed mb-4">
                  "Best IT equipment supplier in Rawalpindi. Quality products with warranty and support."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-full"></div>
                  <div>
                    <p className="font-semibold text-sm text-gray-900">Zainab Raza</p>
                    <p className="text-xs text-gray-600">Director, Medical Center</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-gray-500 text-xs uppercase tracking-widest mb-4">IT INSIGHTS & TIPS</p>
            <h2 className="text-5xl font-serif text-gray-900">Latest IT News & Tips</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                category: 'IT Infrastructure',
                title: 'Essential Network Infrastructure Setup for Small Businesses',
                author: 'Tech Team',
                date: 'Jan 15, 2025',
                image: 'Network+infrastructure+setup+guide+with+routers+switches+cables+professional+IT+installation+diagram+modern+tech+illustration+teal+green+color+scheme'
              },
              {
                category: 'Hardware Repair',
                title: 'Common Computer Problems and How to Fix Them',
                author: 'Repair Experts',
                date: 'Jan 12, 2025',
                image: 'Computer+repair+troubleshooting+guide+with+diagnostic+tools+hardware+components+repair+process+modern+tech+illustration+professional+design'
              },
              {
                category: 'Buying Guide',
                title: 'How to Choose the Right IT Equipment for Your Business',
                author: 'Sales Team',
                date: 'Jan 10, 2025',
                image: 'IT+equipment+buying+guide+with+computers+laptops+servers+selection+criteria+modern+tech+illustration+clean+design'
              }
            ].map((article, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer"
              >
                <div className="relative overflow-hidden" style={{ height: '240px' }}>
                  <img 
                    src={`https://readdy.ai/api/search-image?query=$%7Barticle.image%7D&width=600&height=400&seq=blog-${index}&orientation=landscape`}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute bottom-4 left-4">
                    <span className="inline-block px-4 py-2 bg-teal-500 text-white rounded-full text-xs font-medium">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4 line-clamp-2 leading-snug">
                    {article.title}
                  </h3>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-green-500 rounded-full"></div>
                    <div>
                      <p className="text-sm text-gray-700 font-medium">{article.author}</p>
                      <p className="text-xs text-gray-500">{article.date}</p>
                    </div>
                  </div>
                  <a href="#" className="inline-flex items-center gap-2 text-teal-600 font-medium text-sm hover:gap-3 transition-all whitespace-nowrap">
                    Read More <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact/CTA Section */}
      <section id="contact" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Modern%20IT%20service%20center%20in%20Rawalpindi%20Pakistan%20with%20professional%20technicians%20working%20on%20computers%20servers%20networking%20equipment%20organized%20workspace%20evening%20lighting%20technology%20hub&width=1920&height=1080&seq=cta-service-center&orientation=landscape" 
            alt="IT Service Center"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
        </div>
        
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <h2 className="text-7xl font-black text-white mb-6 tracking-wide uppercase">
            NEED IT SUPPORT?
          </h2>
          
          <p className="text-2xl text-white/90 mb-4 leading-relaxed">
            Infrastructure Setup • Hardware Repair • IT Equipment Sales
          </p>
          <p className="text-xl text-white/80 mb-12 leading-relaxed">
            Door-to-door service in Rawalpindi & surrounding areas
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button 
              onClick={handleCallClick}
              className="group inline-flex items-center gap-4 bg-teal-600 text-white px-12 py-6 rounded-full text-base font-semibold shadow-2xl hover:bg-teal-700 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <span className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-whatsapp-line text-xl text-white"></i>
              </span>
              <span className="uppercase tracking-wide">WHATSAPP US</span>
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
            </button>
            
            <button 
              onClick={handleVisitClick}
              className="group inline-flex items-center gap-4 bg-white text-teal-700 px-12 py-6 rounded-full text-base font-semibold shadow-2xl hover:bg-gray-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <span className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <i className="ri-map-pin-line text-xl text-teal-700"></i>
              </span>
              <span className="uppercase tracking-wide">VISIT US</span>
            </button>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <i className="ri-map-pin-2-fill text-teal-400 text-2xl"></i>
              <p className="text-white text-lg font-semibold">Midway, Rawalpindi, Pakistan</p>
            </div>
            <div className="flex items-center justify-center gap-3 mb-3">
              <i className="ri-whatsapp-fill text-teal-400 text-xl"></i>
              <a 
                href="https://wa.me/923379913671" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-white text-base hover:text-teal-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                +92 337 9913671
              </a>
            </div>
            <p className="text-white/80 text-sm">Serving 50+ companies with quality IT solutions</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/51e5145f106a45f8cb71e40c8e7cc05e/d0b833b3237dbb7c188059d6e6857cdf.png" 
                alt="Northern Tech Logo" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-semibold">Northern Tech</span>
            </div>
            <div className="text-right">
              <p className="text-white/80 mb-2">Complete IT Solutions in Rawalpindi</p>
              <a 
                href="https://wa.me/923379913671" 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-white hover:text-teal-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-fill text-lg"></i>
                <span>+92 337 9913671</span>
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div>
              <h4 className="text-lg font-semibold mb-6">IT Services</h4>
              <ul className="space-y-4">
                <li><a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Infrastructure Setup</a></li>
                <li><a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Hardware Repair</a></li>
                <li><a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">IT Equipment Sales</a></li>
                <li><a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Network Solutions</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Company</h4>
              <ul className="space-y-4">
                <li><a href="#about" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">About Us</a></li>
                <li><a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Our Team</a></li>
                <li><a href="#contact" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Contact</a></li>
                <li><a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Careers</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Resources</h4>
              <ul className="space-y-4">
                <li><a href="#blog" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Blog</a></li>
                <li><a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">IT Tips</a></li>
                <li><a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Product Catalog</a></li>
                <li><a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Support</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
              <div className="space-y-4">
                <a 
                  href="https://wa.me/923379913671" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  <span className="whitespace-nowrap">+92 337 9913671</span>
                </a>
                <div className="flex items-center gap-3 text-white/80">
                  <i className="ri-map-pin-line text-xl"></i>
                  <span className="text-sm">Midway, Rawalpindi</span>
                </div>
                <div className="flex items-center gap-4 pt-4">
                  <a 
                    href="https://wa.me/923379913671" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-whatsapp-fill text-2xl"></i>
                  </a>
                  <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-facebook-fill text-2xl"></i>
                  </a>
                  <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-linkedin-fill text-2xl"></i>
                  </a>
                  <a href="#" className="text-white/80 hover:text-white transition-colors cursor-pointer">
                    <i className="ri-instagram-fill text-2xl"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center pt-12 border-t border-white/20">
            <div className="rounded-2xl overflow-hidden">
              <img 
                src="https://readdy.ai/api/search-image?query=IT%20service%20workshop%20in%20Rawalpindi%20Pakistan%20with%20repair%20equipment%20computers%20servers%20organized%20workspace%20professional%20IT%20technicians%20working%20evening%20lighting&width=500&height=350&seq=footer-workshop&orientation=landscape" 
                alt="IT Service Workshop"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="text-center">
              <p className="text-3xl font-serif text-white leading-relaxed">
                Door-to-Door<br />IT Service<br />in Rawalpindi
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-3">Get IT Updates</h4>
              <p className="text-sm text-white/80 mb-4">Subscribe for IT tips and special offers</p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Your email address"
                  className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-sm text-white placeholder-white/50 focus:outline-none focus:border-white/40 transition-colors"
                />
                <button className="px-6 py-3 bg-white text-teal-700 rounded-lg font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/20 text-center">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-white/60 hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
